import React from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Renders category-specific fields defined by the backend field_schema endpoint.
 * Each field is a plain object: { key, label_lv, label_en, type, required, options, placeholder, visible_if }.
 * Values live in `values` (a plain object) and are updated via `onChange(key, value)`.
 */
export default function CustomFieldsRenderer({ fields, values, onChange }) {
  const { language } = useLanguage();
  if (!Array.isArray(fields) || fields.length === 0) return null;

  // Filter fields by visible_if condition (simple key==value match)
  const isVisible = (field) => {
    if (!field.visible_if) return true;
    return Object.entries(field.visible_if).every(
      ([k, v]) => values[k] === v,
    );
  };

  const lbl = (o) => (language === 'lv' ? o.lv : o.en);
  const fieldLabel = (f) => (language === 'lv' ? f.label_lv : f.label_en);

  return (
    <div className="space-y-4 pt-4 border-t border-[#E5E4E0]">
      <h3 className="font-outfit font-semibold text-lg text-[#121212]">
        {language === 'lv' ? 'Specifiskās īpašības' : 'Specific properties'}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {fields.filter(isVisible).map((field) => {
          const val = values[field.key];
          const reqMark = field.required ? ' *' : '';

          if (field.type === 'checkbox') {
            return (
              <div key={field.key} className="flex items-center gap-2 md:col-span-2">
                <Checkbox
                  id={`cf-${field.key}`}
                  checked={!!val}
                  onCheckedChange={(c) => onChange(field.key, !!c)}
                  data-testid={`cf-${field.key}`}
                />
                <Label htmlFor={`cf-${field.key}`} className="cursor-pointer text-sm">
                  {fieldLabel(field)}{reqMark}
                </Label>
              </div>
            );
          }

          if (field.type === 'textarea') {
            return (
              <div key={field.key} className="md:col-span-2">
                <Label className="text-sm mb-1.5 block">{fieldLabel(field)}{reqMark}</Label>
                <Textarea
                  value={val || ''}
                  onChange={(e) => onChange(field.key, e.target.value)}
                  placeholder={field.placeholder || ''}
                  rows={3}
                  className="border-[#E5E4E0]"
                  data-testid={`cf-${field.key}`}
                />
              </div>
            );
          }

          if (field.type === 'select') {
            return (
              <div key={field.key}>
                <Label className="text-sm mb-1.5 block">{fieldLabel(field)}{reqMark}</Label>
                <Select
                  value={val || ''}
                  onValueChange={(v) => onChange(field.key, v)}
                >
                  <SelectTrigger data-testid={`cf-${field.key}`} className="border-[#E5E4E0]">
                    <SelectValue placeholder={language === 'lv' ? 'Izvēlies...' : 'Select...'} />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {(field.options || []).map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {lbl(opt)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            );
          }

          if (field.type === 'multiselect') {
            const selected = Array.isArray(val) ? val : [];
            const toggle = (optValue) => {
              const next = selected.includes(optValue)
                ? selected.filter((x) => x !== optValue)
                : [...selected, optValue];
              onChange(field.key, next);
            };
            return (
              <div key={field.key} className="md:col-span-2">
                <Label className="text-sm mb-1.5 block">{fieldLabel(field)}{reqMark}</Label>
                <div className="flex flex-wrap gap-2" data-testid={`cf-${field.key}`}>
                  {(field.options || []).map((opt) => {
                    const active = selected.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => toggle(opt.value)}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                          active
                            ? 'bg-[#121212] text-white border-[#121212]'
                            : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#121212]'
                        }`}
                        data-testid={`cf-${field.key}-${opt.value}`}
                      >
                        {lbl(opt)}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          }

          // text, number, url (default input)
          return (
            <div key={field.key}>
              <Label className="text-sm mb-1.5 block">{fieldLabel(field)}{reqMark}</Label>
              <Input
                type={field.type === 'number' ? 'number' : field.type === 'url' ? 'url' : 'text'}
                value={val ?? ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (field.type === 'number') {
                    onChange(field.key, raw === '' ? '' : Number(raw));
                  } else {
                    onChange(field.key, raw);
                  }
                }}
                placeholder={field.placeholder || ''}
                className="border-[#E5E4E0]"
                data-testid={`cf-${field.key}`}
                step={field.type === 'number' ? 'any' : undefined}
              />
            </div>
          );
        })}
      </div>

      <p className="text-xs text-[#5C5C5C]">
        * {language === 'lv' ? 'Obligāti lauki' : 'Required fields'}
      </p>
    </div>
  );
}
